import type {
  ChatActivityAnswer,
  ExecutiveSummaryAnswer,
  InputStyle4Answer,
  PersonaEntryAnswer,
} from '@app/@types/redux/answer';
import type {
  CanvasOptions,
  ChatActivityOptions,
  TextareaOptions,
} from '@app/@types/redux/pageElement';
import type { DependeeValue } from '@app/@types/redux/project';
import type { PageElementProps } from '@components/Projects/TakeProject';
import { NOTIFICATION_AUDIO_URL, PAGE_ELEMENT } from '@libs/constants/constants';
import logger from '@libs/log';
import { nanoid } from '@reduxjs/toolkit';
import { useAppSelector } from '@state/redux/store';
import { getDependeeValues, getPageElementDisabledState } from '@state/selectors/projects';
import Api from '@state/utils/Api';
import { type Message } from '@state/utils/openai';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import ChatBox from './ChatBox';
import ChatMenu from './ChatMenu';
import type { ChatItem } from './TempChat';
import './index.scss';
import { Chats } from './tempConstants';

const DEFAULT_FAVICON = '/favicon.ico';
const NOTIFICATION_FAVICON = '/favicon-notification.ico'; // Update this path to your red dot favicon

// Utility function to change favicon
const changeFavicon = (href: string) => {
  const link = document.querySelector("link[rel*='icon']");
  if (link) {
    (link as HTMLLinkElement).href = href;
  } else {
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.href = href;
    document.head.appendChild(newLink);
  }
};

const Chat: React.FC<PageElementProps> = (props) => {
  const { answer, pageElement, state } = props;

  const currentAnswer = props.currentAnswer as ChatActivityAnswer | undefined;

  const { dependees, id: pageElementId } = pageElement.attributes;

  const dependentValues = getDependeeValues(dependees, [], state) as DependeeValue[];
  const disabled = getPageElementDisabledState(state, pageElement.id);

  // Memoize dependentAnswer and modelAnswer computation
  const { dependentAnswer, modelAnswer } = useMemo(() => {
    let computedDependentAnswer = '';
    let computedModelAnswer = '';

    if (dependentValues.length > 0) {
      if (dependentValues[0].dependeePageElement.elementType === PAGE_ELEMENT.PERSONA_ENTRY) {
        // Processing Persona activity
        const personaEntry = dependentValues[0].answer as PersonaEntryAnswer;
        const inputFieldAnswers = personaEntry.inputFields
          .map((field) => `${field.fieldLabel}: ${field.fieldValue}`)
          .join('\n');
        const longTextareaAnswers = personaEntry.longTextareaFields
          .map((field) => `${field.fieldLabel}: ${field.fieldValue}`)
          .join('\n');

        computedDependentAnswer = `Input Fields:\n${inputFieldAnswers}\nLong Textarea Fields:\n${longTextareaAnswers}`;
      }
      if (
        (dependentValues[0].dependeePageElement.options as CanvasOptions).canvasType ===
        'swot_canvas_creator'
      ) {
        // Processing Short-Answer activity
        const strengthsAnswer = dependentValues.find(
          (dv) =>
            dv.pageElement &&
            (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'strengths'
        )?.answer as InputStyle4Answer | undefined;
        const strengths = strengthsAnswer?.values.map((v) => v.value).join(', ') ?? '';

        const weaknessesAnswer = dependentValues.find(
          (dv) =>
            dv.pageElement &&
            (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'weaknesses'
        )?.answer as InputStyle4Answer | undefined;
        const weaknesses = weaknessesAnswer?.values.map((v) => v.value).join(', ') ?? '';

        const opportunitiesAnswer = dependentValues.find(
          (dv) =>
            dv.pageElement &&
            (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'opportunities'
        )?.answer as InputStyle4Answer | undefined;
        const opportunities = opportunitiesAnswer?.values.map((v) => v.value).join(', ') ?? '';

        const threatsAnswer = dependentValues.find(
          (dv) =>
            dv.pageElement &&
            (dv.pageElement.options as TextareaOptions).label.toLowerCase() === 'threats'
        )?.answer as InputStyle4Answer | undefined;
        const threats = threatsAnswer?.values.map((v) => v.value).join(', ') ?? '';

        computedDependentAnswer = `Strengths: ${strengths}\nWeaknesses: ${weaknesses}\nOpportunities: ${opportunities}\nThreats: ${threats}`;
      }
      if (
        (dependentValues[0].dependeePageElement.options as CanvasOptions).canvasType ===
          'custom_canvas_creator' ||
        dependentValues[0].dependeePageElement.elementType === PAGE_ELEMENT.EXECUTIVE_SUMMARY
      ) {
        computedDependentAnswer = '';
        dependentValues.forEach((dv, index) => {
          const values = dv.answer as ExecutiveSummaryAnswer | string;
          if (values && typeof values === 'object') {
            Object.values(values).forEach((value, index) => {
              computedDependentAnswer += `<QUESTION${String(index + 1)}>${
                value.question
              }</QUESTION${String(index + 1)}>\n<ANSWER${String(index + 1)}>${
                value.answer
              }</ANSWER${String(index + 1)}>\n`;
            });
          } else if (values && typeof values === 'string') {
            // Processing Short-Answer activity
            const answer = values;
            const question = (dv.pageElement?.options as TextareaOptions).label;
            computedDependentAnswer += `<QUESTION${String(index + 1)}>${question}</QUESTION${String(
              index + 1
            )}>\n<ANSWER${String(index + 1)}>${answer}</ANSWER${String(index + 1)}>\n`;
          }
        });
        const questions =
          (pageElement.attributes.options as ChatActivityOptions).activityData
            .submissionQuestions ??
          (pageElement.attributes.options as ChatActivityOptions).activityData.questions?.map(
            (q) => ({ ...q, visible: true })
          ) ??
          [];
        computedModelAnswer = questions
          .filter((q) => q.visible)
          .map(
            (question, _i) =>
              `\n${String(_i + 1)}. ${question.question}\n${question.modelAnswer ?? ''}`
          )
          .join('\n');
      }
    }

    return {
      dependentAnswer: computedDependentAnswer,
      modelAnswer: computedModelAnswer,
    };
  }, [dependentValues, pageElement.attributes.options]);

  // Store answer function in ref to avoid dependency issues
  const answerRef = useRef(answer);
  useEffect(() => {
    answerRef.current = answer;
  }, [answer]);

  // Store latest answer state in ref to prevent race conditions from concurrent updates
  const currentAnswerRef = useRef<ChatActivityAnswer | undefined>(currentAnswer);
  useEffect(() => {
    currentAnswerRef.current = currentAnswer;
  }, [currentAnswer]);

  // Track previous dependentAnswer to avoid unnecessary updates
  const prevDependentAnswerRef = useRef<string>('');

  // Update currentAnswer when dependentAnswer changes
  useEffect(() => {
    if (dependentAnswer !== prevDependentAnswerRef.current) {
      prevDependentAnswerRef.current = dependentAnswer;
      if (dependentAnswer !== currentAnswerRef.current?.dependentAnswer) {
        const latestAnswer = currentAnswerRef.current;
        answerRef.current({
          ...latestAnswer,
          dependentAnswer,
        } as ChatActivityAnswer);
      }
    }
  }, [dependentAnswer]);

  const [loading, setLoading] = useState(false);

  const [searchboxValue, setSearchboxValue] = useState('');

  // Memoize user object extraction
  const user = useMemo(
    () => (pageElement.attributes.options as ChatActivityOptions).chatActivityData.users[0],
    [pageElement.attributes.options]
  );

  const [selectedChat, setSelectedChat] = useState(user.id);

  // Memoize event handlers
  const handleSearchboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchboxValue(e.target.value);
  }, []);

  const handleChatSelect = useCallback((chatId: string) => {
    setSelectedChat(chatId);
  }, []);

  // Store original favicon to revert later
  const originalFaviconRef = useRef<string | null>(null);
  const faviconTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Function to play notification audio and change favicon
  const playNotificationAudio = useCallback(() => {
    try {
      const audio = new Audio(NOTIFICATION_AUDIO_URL);
      audio.play().catch((error: unknown) => {
        // Silently handle autoplay restrictions
        logger.error(error);
      });

      // Change favicon to notification favicon
      const linkElement = document.querySelector("link[rel*='icon']");
      const currentFavicon = linkElement ? (linkElement as HTMLLinkElement).href : DEFAULT_FAVICON;
      if (!originalFaviconRef.current || originalFaviconRef.current === NOTIFICATION_FAVICON) {
        originalFaviconRef.current = currentFavicon;
      }
      changeFavicon(NOTIFICATION_FAVICON);

      // Clear any existing timeout
      if (faviconTimeoutRef.current) {
        clearTimeout(faviconTimeoutRef.current);
        faviconTimeoutRef.current = null;
      }

      // Revert favicon after 5 seconds
      faviconTimeoutRef.current = setTimeout(() => {
        if (originalFaviconRef.current) {
          changeFavicon(originalFaviconRef.current);
        }
        faviconTimeoutRef.current = null;
      }, 5000);
    } catch (error: unknown) {
      logger.error(error);
    }
  }, []);

  // Revert favicon on window focus (user is back to the tab)
  useEffect(() => {
    const handleFocus = () => {
      if (originalFaviconRef.current && originalFaviconRef.current !== NOTIFICATION_FAVICON) {
        changeFavicon(originalFaviconRef.current);
        if (faviconTimeoutRef.current) {
          clearTimeout(faviconTimeoutRef.current);
          faviconTimeoutRef.current = null;
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      if (faviconTimeoutRef.current) {
        clearTimeout(faviconTimeoutRef.current);
        faviconTimeoutRef.current = null;
      }
      if (originalFaviconRef.current && originalFaviconRef.current !== NOTIFICATION_FAVICON) {
        changeFavicon(originalFaviconRef.current);
      }
    };
  }, []);

  const [messages, setMessages] = useState<ChatItem[]>(currentAnswer?.messages ?? []);

  // Memoize chats array
  const chats = useMemo(
    () => [
      {
        id: user.id,
        name: user.name,
        lastMessage: messages[messages.length - 1]?.message ?? '',
        lastMessageTime: moment(messages[messages.length - 1]?.timestamp ?? '').format('hh:mm A'),
        profilePicture: user.profileImage,
        messageStatus: 'default',
        online: true,
      },
      ...Chats,
    ],
    [user, messages]
  );

  // Memoize chatDuration calculation
  const chatDuration = useMemo(
    () => Number((pageElement.attributes.options as ChatActivityOptions).chatActivityData.duration),
    [pageElement.attributes.options]
  );

  // Use ref callback pattern to keep messagesRef in sync
  const messagesRef = useRef<ChatItem[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const projectId = useAppSelector((state) => state.projects.currentProject.project.id);

  // Refs for cleanup and preventing duplicate calls
  const isMountedRef = useRef(true);
  const initialMessageSentRef = useRef(false);
  const isProcessingRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // First message API call effect
  useEffect(() => {
    if (
      messagesRef.current.length === 0 &&
      !loading &&
      !initialMessageSentRef.current &&
      dependentAnswer &&
      modelAnswer &&
      pageElementId &&
      projectId &&
      isMountedRef.current
    ) {
      initialMessageSentRef.current = true;
      setLoading(true);
      isProcessingRef.current = true;

      const promptArray: Message[] = [];

      Api.getChatActivityCompletions(
        projectId,
        promptArray,
        pageElementId,
        dependentAnswer,
        modelAnswer
      )
        .then((response) => {
          // Only process if component is still mounted
          if (!isMountedRef.current) {
            return;
          }
          const message = response.data.message;
          setMessages((prev) => [
            ...prev,
            {
              id: nanoid(),
              message,
              sender: 'assistant',
              timestamp: new Date().toISOString(),
              openedAt: null,
            },
          ]);
          // Play notification audio when assistant message arrives
          playNotificationAudio();
        })
        .catch((error: unknown) => {
          // Only log if component is still mounted
          if (isMountedRef.current) {
            logger.error(error);
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setLoading(false);
            isProcessingRef.current = false;
          }
        });
    }
  }, [dependentAnswer, modelAnswer, pageElementId, projectId, playNotificationAudio]);

  // Track processed user message IDs to prevent duplicate processing
  const processedUserMessageIdsRef = useRef<Set<string>>(new Set());

  // Track previous messages to avoid unnecessary answer updates
  const prevMessagesRef = useRef<ChatItem[]>(messages);
  const prevMessagesLengthRef = useRef<number>(messages.length);

  // User message response effect - only process new user messages
  useEffect(() => {
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : undefined;

    // Check if this is a new user message that hasn't been processed
    const isNewUserMessage =
      lastMessage &&
      lastMessage.sender === 'user' &&
      !processedUserMessageIdsRef.current.has(lastMessage.id);

    const shouldProcess =
      isNewUserMessage &&
      !loading &&
      !isProcessingRef.current &&
      dependentAnswer &&
      modelAnswer &&
      pageElementId &&
      projectId &&
      isMountedRef.current;

    if (shouldProcess) {
      // Mark this message as processed
      processedUserMessageIdsRef.current.add(lastMessage.id);

      setLoading(true);
      isProcessingRef.current = true;

      const gptMessages = messages.map((message) => {
        return {
          role: message.sender,
          content: message.message,
        };
      });

      Api.getChatActivityCompletions(
        projectId,
        gptMessages,
        pageElementId,
        dependentAnswer,
        modelAnswer
      )
        .then((response) => {
          // Only process if component is still mounted
          if (!isMountedRef.current) {
            return;
          }
          const message = response.data.message;
          setMessages((prev) => [
            ...prev,
            {
              id: nanoid(),
              message,
              sender: 'assistant',
              timestamp: new Date().toISOString(),
              openedAt: null,
            },
          ]);
          // Play notification audio when assistant message arrives
          playNotificationAudio();
        })
        .catch((error: unknown) => {
          // Only log if component is still mounted
          if (isMountedRef.current) {
            logger.error(error);
            // Remove from processed set on error so it can be retried
            processedUserMessageIdsRef.current.delete(lastMessage.id);
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setLoading(false);
            isProcessingRef.current = false;
          }
        });
    }
  }, [
    messages,
    loading,
    dependentAnswer,
    modelAnswer,
    pageElementId,
    projectId,
    playNotificationAudio,
  ]);

  // Separate effect to update answer when messages change (without triggering API calls)
  useEffect(() => {
    // Update answer only when messages actually changed
    const messagesChanged =
      messages.length !== prevMessagesLengthRef.current ||
      messages.some((msg, idx) => msg.id !== prevMessagesRef.current[idx]?.id);

    if (messagesChanged && isMountedRef.current) {
      prevMessagesRef.current = messages;
      prevMessagesLengthRef.current = messages.length;
      const latestAnswer = currentAnswerRef.current;
      answerRef.current({
        ...latestAnswer,
        dependentAnswer,
        messages,
      } as ChatActivityAnswer);
    }
  }, [messages, dependentAnswer]);

  // Chat duration timer effect - end chat after duration expires
  useEffect(() => {
    const firstUserMessage = messages.find((message) => message.sender === 'user');

    if (
      firstUserMessage &&
      !currentAnswerRef.current?.chatEnded &&
      chatDuration > 0 &&
      isMountedRef.current
    ) {
      const interval = setInterval(() => {
        if (!isMountedRef.current) {
          clearInterval(interval);
          return;
        }

        const timeElapsed = new Date().getTime() - new Date(firstUserMessage.timestamp).getTime();
        const durationMs = chatDuration * 60 * 1000;

        if (timeElapsed > durationMs) {
          clearInterval(interval);

          if (isProcessingRef.current) {
            return;
          }

          setLoading(true);
          isProcessingRef.current = true;

          Api.getChatActivityEndChat(
            projectId,
            pageElementId,
            messagesRef.current.map((message) => ({
              role: message.sender,
              content: message.message,
            }))
          )
            .then((response) => {
              // Only process if component is still mounted
              if (!isMountedRef.current) {
                return;
              }
              const message = response.data.message;
              setMessages((prev) => [
                ...prev,
                {
                  id: nanoid(),
                  message,
                  sender: 'assistant',
                  timestamp: new Date().toISOString(),
                  openedAt: null,
                },
              ]);

              const latestAnswer = currentAnswerRef.current;
              answerRef.current({
                ...latestAnswer,
                chatEnded: true,
              } as ChatActivityAnswer);
              // Play notification audio when assistant message arrives
              playNotificationAudio();
            })
            .catch((error: unknown) => {
              // Only log if component is still mounted
              if (isMountedRef.current) {
                logger.error(error);
              }
            })
            .finally(() => {
              if (isMountedRef.current) {
                setLoading(false);
                isProcessingRef.current = false;
              }
            });
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [messages, currentAnswer, chatDuration, projectId, pageElementId, playNotificationAudio]);

  return (
    <div className="chat-container">
      <div className="left-side">
        <div className="chat-search-box-container">
          <div>
            <FaSearch className="chat-search-box-fixed-label" />
          </div>
          <div className="chat-input-wrapper">
            <input
              type="text"
              value={searchboxValue}
              onChange={handleSearchboxChange}
              placeholder="Search..."
            />
          </div>
        </div>
        {chats.map((chat) => (
          <ChatMenu
            key={chat.id}
            chat={chat}
            selected={selectedChat === chat.id}
            handleOnClick={() => {
              handleChatSelect(chat.id);
            }}
            disabled={disabled}
          />
        ))}
      </div>
      <div className="right-side">
        <ChatBox
          messages={messages}
          avatar={user}
          setMessages={setMessages}
          disabled={currentAnswer?.chatEnded ?? disabled}
          loading={loading}
        />
      </div>
    </div>
  );
};
export default Chat;
